import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';

const COLLECTION_TABS = [
  { handle: 'all-products', label: 'All Products' },
  { handle: 'mattresses', label: 'Mattresses' },
  { handle: 'pillow', label: 'Pillows' },
  { handle: 'bedding', label: 'Bedding' },
  { handle: 'bedsheets', label: 'Bedsheets' },
  { handle: 'support-mobility', label: 'Support & Mobility' },
  { handle: 'petbed', label: 'Dog Beds' },
  { handle: 'dog-bed-covers', label: 'Dog Bed Covers' },
  { handle: 'bed', label: 'Beds' },
  { handle: 'recliner', label: 'Recliner' },
];

function Inventory() {
  const [loading, setLoading] = useState(false);
  const [inventoryData, setInventoryData] = useState(null);
  const [inventoryFilter, setInventoryFilter] = useState('all');
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryCollection, setInventoryCollection] = useState('all-products');
  const [locationId, setLocationId] = useState(localStorage.getItem('selectedLocation') || '');
  const [locationType, setLocationType] = useState('');

  // Customise modal state
  const [showCustomiseModal, setShowCustomiseModal] = useState(false);
  const [customiseItem, setCustomiseItem] = useState(null);
  const [customLength, setCustomLength] = useState('');
  const [customWidth, setCustomWidth] = useState('');
  const [standardDims, setStandardDims] = useState(null);
  const [matchedVariant, setMatchedVariant] = useState(null);
  const [matchedDims, setMatchedDims] = useState(null);

  // Delivery timeline modal state
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryItem, setDeliveryItem] = useState(null);
  const [deliveryPincode, setDeliveryPincode] = useState('');
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryResults, setDeliveryResults] = useState(null);

  // Delivery check inside Customise modal
  const [custDeliveryPincode, setCustDeliveryPincode] = useState('');
  const [custDeliveryLoading, setCustDeliveryLoading] = useState(false);
  const [custDeliveryResults, setCustDeliveryResults] = useState(null);

  // MTO badge state
  const [mtoMap, setMtoMap] = useState({});
  const mtoControllerRef = useRef(null);

  // EasyEcom inventory state
  const [easyEcom, setEasyEcom] = useState({ loading: false, mapped: false, quantities: {}, fetchedAt: null, locationName: '', error: null, refreshing: false });
  const [syncingSkus, setSyncingSkus] = useState({});
  const eeTimerRef = useRef(null);

  const fetchEasyEcom = async (refresh = false) => {
    if (!locationId) return;
    clearTimeout(eeTimerRef.current);
    setEasyEcom(prev => ({ ...prev, loading: !prev.fetchedAt, refreshing: refresh || prev.refreshing, error: null }));
    try {
      const res = await api.get('/easyecom/inventory', { params: { locationId, ...(refresh ? { refresh: 1 } : {}) } });
      // 202 = server is building the snapshot (first ~90s after boot) — poll
      if (res.status === 202 || res.data.building) {
        setEasyEcom(prev => ({ ...prev, loading: true, mapped: true, error: null }));
        eeTimerRef.current = setTimeout(() => fetchEasyEcom(), 10000);
        return;
      }
      setEasyEcom({
        loading: false,
        mapped: res.data.mapped,
        quantities: res.data.quantities || {},
        fetchedAt: res.data.fetchedAt || null,
        locationName: res.data.easyEcomLocationName || '',
        error: null,
        refreshing: !!res.data.refreshing
      });
      // A refresh is running in background — poll until it lands
      if (res.data.refreshing) {
        eeTimerRef.current = setTimeout(() => fetchEasyEcom(), 15000);
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'EasyEcom unavailable';
      setEasyEcom({ loading: false, mapped: false, quantities: {}, fetchedAt: null, locationName: '', error: msg, refreshing: false });
    }
  };

  useEffect(() => {
    fetchEasyEcom();
    return () => clearTimeout(eeTimerRef.current);
    // eslint-disable-next-line
  }, [locationId]);

  const handleEasyEcomSync = async (item) => {
    if (!item.sku || syncingSkus[item.sku]) return;
    setSyncingSkus(prev => ({ ...prev, [item.sku]: true }));
    try {
      const res = await api.post('/easyecom/sync', { locationId, sku: item.sku });
      const { shopifyAvailable, easyEcomAvailable } = res.data;
      // Reflect new quantity in both the Shopify stock column and the EasyEcom column
      setInventoryData(prev => prev ? {
        ...prev,
        items: prev.items.map(i => i.sku === item.sku ? { ...i, quantity: shopifyAvailable } : i)
      } : prev);
      setEasyEcom(prev => ({
        ...prev,
        quantities: { ...prev.quantities, [item.sku]: { ...(prev.quantities[item.sku] || {}), available: easyEcomAvailable } }
      }));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to sync inventory from EasyEcom');
    }
    setSyncingSkus(prev => { const next = { ...prev }; delete next[item.sku]; return next; });
  };

  useEffect(() => {
    const handleLocationChange = (e) => {
      setLocationId(e.detail);
    };
    window.addEventListener('locationchange', handleLocationChange);
    return () => window.removeEventListener('locationchange', handleLocationChange);
  }, []);

  // Fetch location type (store vs warehouse)
  useEffect(() => {
    if (!locationId) return;
    setLocationType('');
    api.get('/locations').then(res => {
      const locs = res.data.locations || [];
      const current = locs.find(l => l.id?.toString() === locationId?.toString());
      setLocationType((current?.type || 'store').toLowerCase());
    }).catch(() => setLocationType('store'));
  }, [locationId]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/inventory', { params: { locationId, collection: inventoryCollection } });
      setInventoryData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line
  }, [locationId, inventoryCollection]);

  const isWarehouse = locationType === 'warehouse';

  // Fetch MTO status after inventory loads (warehouse only)
  useEffect(() => {
    if (!isWarehouse) { setMtoMap({}); return; }
    if (!inventoryData || !inventoryData.items || inventoryData.items.length === 0) return;
    if (mtoControllerRef.current) mtoControllerRef.current.abort();
    const controller = new AbortController();
    mtoControllerRef.current = controller;

    const continueVariantIds = inventoryData.items
      .filter(item => item.continueSelling && item.variantId)
      .map(item => item.variantId);
    if (continueVariantIds.length === 0) { setMtoMap({}); return; }

    (async () => {
      for (let i = 0; i < continueVariantIds.length; i += 250) {
        if (controller.signal.aborted) return;
        const chunk = continueVariantIds.slice(i, i + 250);
        try {
          const res = await api.post('/products/variants/batch-is-mto', { variantIds: chunk }, { signal: controller.signal });
          if (!controller.signal.aborted) {
            setMtoMap(prev => ({ ...prev, ...res.data.results }));
          }
        } catch (e) {
          if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') {
            console.error('Error fetching MTO batch:', e);
          }
        }
      }
    })();

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData, isWarehouse]);

  const filteredInventory = useMemo(() => {
    if (!inventoryData) return [];
    let items = inventoryData.items;
    // For stores: treat continueSelling (MTO) items with qty<=0 as out of stock
    if (inventoryFilter === 'mto') items = items.filter(i => i.continueSelling && i.quantity <= 0 && isWarehouse);
    else if (inventoryFilter === 'out') items = items.filter(i => i.quantity <= 0 && (!i.continueSelling || !isWarehouse));
    else if (inventoryFilter === 'low') items = items.filter(i => i.quantity > 0 && i.quantity <= 5);
    else if (inventoryFilter === 'in') items = items.filter(i => i.quantity > 0 || (i.continueSelling && isWarehouse));
    if (inventorySearch.length >= 2) {
      const q = inventorySearch.toLowerCase();
      items = items.filter(i =>
        i.productTitle.toLowerCase().includes(q) ||
        (i.variantTitle && i.variantTitle.toLowerCase().includes(q)) ||
        (i.sku && i.sku.toLowerCase().includes(q))
      );
    }
    return items;
  }, [inventoryData, inventoryFilter, inventorySearch, isWarehouse]);

  // Live fallback: EasyEcom's bulk catalog feed omits some products (hidden/
  // inactive masters), so SKUs can be missing from the snapshot while the
  // per-SKU API knows them. When the current view is narrow enough, fetch the
  // missing SKUs live and merge them in — the EE column fills and Sync enables.
  const liveAttemptedRef = useRef(new Set());
  const liveTimerRef = useRef(null);
  useEffect(() => { liveAttemptedRef.current = new Set(); }, [locationId]);
  useEffect(() => {
    if (!easyEcom.mapped || easyEcom.loading || !easyEcom.fetchedAt) return;
    if (!filteredInventory.length || filteredInventory.length > 50) return;
    const missing = [...new Set(
      filteredInventory
        .map(i => i.sku)
        .filter(sku => sku && !easyEcom.quantities[sku] && !liveAttemptedRef.current.has(sku))
    )].slice(0, 25);
    if (!missing.length) return;
    clearTimeout(liveTimerRef.current);
    liveTimerRef.current = setTimeout(async () => {
      missing.forEach(sku => liveAttemptedRef.current.add(sku));
      try {
        const res = await api.post('/easyecom/live', { locationId, skus: missing });
        const got = res.data?.quantities || {};
        if (Object.keys(got).length) {
          setEasyEcom(prev => ({ ...prev, quantities: { ...prev.quantities, ...got } }));
        }
      } catch (e) { /* non-fatal — cells simply keep showing “—” */ }
    }, 600);
    return () => clearTimeout(liveTimerRef.current);
    // eslint-disable-next-line
  }, [filteredInventory, easyEcom.mapped, easyEcom.loading, easyEcom.fetchedAt, locationId]);

  const SummaryCard = ({ label, value, sub, color = '#c88346' }) => (
    <div style={{
      background: '#faf7f3',   borderRadius: '16px', padding: '20px', border: '1px solid #ece0d1', boxShadow: '0 4px 24px rgba(26, 26, 46, 0.06)',
      flex: 1, minWidth: '180px'
    }}>
      <div style={{ fontSize: '12px', color: '#8b7d6e', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
        {label}
      </div>
      <div style={{ fontWeight: '700', color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: String(value).length > 10 ? '20px' : '28px' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{sub}</div>}
    </div>
  );

  // Mattress dimension helpers
  const parseMattressDimensions = (variantTitle) => {
    const match3 = variantTitle.match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/);
    if (match3) return { length: parseInt(match3[1]), width: parseInt(match3[2]), height: parseInt(match3[3]) };
    const match2 = variantTitle.match(/(\d+)\s*x\s*(\d+)/);
    if (match2) return { length: parseInt(match2[1]), width: parseInt(match2[2]), height: null };
    return null;
  };

  const findMatchingVariant = (variants, length, width, originalHeight) => {
    const variantsWithDims = variants
      .map(v => ({ variant: v, dims: parseMattressDimensions(v.title) }))
      .filter(v => v.dims !== null);
    const candidates = variantsWithDims.filter(
      v => v.dims.length >= length && v.dims.width >= width
    );
    if (candidates.length === 0) return null;
    // Height must match the original variant; only fall back to other heights
    // when no same-height variant covers the requested size
    const sameHeight = originalHeight
      ? candidates.filter(v => v.dims.height === originalHeight)
      : [];
    const pool = sameHeight.length > 0 ? sameHeight : candidates;
    // Prefer the closest length, then the closest width
    pool.sort((a, b) => {
      if (a.dims.length !== b.dims.length) return a.dims.length - b.dims.length;
      return a.dims.width - b.dims.width;
    });
    return pool[0];
  };

  const handleDimensionChange = (newLength, newWidth, variants) => {
    const l = parseInt(newLength);
    const w = parseInt(newWidth);
    if (isNaN(l) || isNaN(w)) {
      setMatchedVariant(null);
      setMatchedDims(null);
      return;
    }
    const match = findMatchingVariant(variants, l, w, standardDims?.height);
    if (match) {
      setMatchedVariant(match.variant);
      setMatchedDims(match.dims);
    } else {
      setMatchedVariant(null);
      setMatchedDims(null);
    }
  };

  const handleOpenCustomise = async (item) => {
    const dims = parseMattressDimensions(item.variantTitle);
    setCustomiseItem(item);
    setCustomLength(dims ? dims.length : '');
    setCustomWidth(dims ? dims.width : '');
    setStandardDims(dims);
    setMatchedVariant(null);
    setMatchedDims(null);
    setCustDeliveryPincode('');
    setCustDeliveryResults(null);
    setShowCustomiseModal(true);

    // Fetch all variants (products can have 100+ variants, embedded list is capped at 100)
    try {
      const res = await api.get(`/products/${item.productId}/variants`);
      const allVariants = res.data.variants || [];
      const updated = { ...item, variants: allVariants };
      setCustomiseItem(updated);
      if (dims) {
        const match = findMatchingVariant(allVariants, dims.length, dims.width, dims.height);
        setMatchedVariant(match ? match.variant : null);
        setMatchedDims(match ? match.dims : null);
      }
    } catch (e) {
      // Fallback to embedded variants
      if (dims) {
        const match = findMatchingVariant(item.variants || [], dims.length, dims.width, dims.height);
        setMatchedVariant(match ? match.variant : null);
        setMatchedDims(match ? match.dims : null);
      }
    }
  };

  const handleOpenDelivery = (item) => {
    setDeliveryItem(item);
    setDeliveryPincode('');
    setDeliveryResults(null);
    setShowDeliveryModal(true);
  };

  const handleCheckDelivery = async () => {
    if (!deliveryPincode || deliveryPincode.length !== 6) {
      alert('Please enter a valid 6-digit pincode');
      return;
    }
    setDeliveryLoading(true);
    setDeliveryResults(null);
    try {
      const res = await api.post('/checkout/verify-pincode', {
        pincode: deliveryPincode,
        items: [{ sku: deliveryItem.sku, title: deliveryItem.productTitle, isCustom: false, isMto: false, isCustomSize: false }]
      });
      setDeliveryResults(res.data);
    } catch (e) {
      setDeliveryResults({ success: false, results: [{ deliverable: false, error: 'Failed to check delivery' }] });
    }
    setDeliveryLoading(false);
  };

  const handleCustCheckDelivery = async () => {
    if (!custDeliveryPincode || custDeliveryPincode.length !== 6) {
      alert('Please enter a valid 6-digit pincode');
      return;
    }
    const sku = matchedVariant ? matchedVariant.sku : customiseItem.sku;
    const isCustom = matchedVariant && matchedDims &&
      (parseInt(customLength) !== matchedDims.length || parseInt(customWidth) !== matchedDims.width);
    setCustDeliveryLoading(true);
    setCustDeliveryResults(null);
    try {
      const res = await api.post('/checkout/verify-pincode', {
        pincode: custDeliveryPincode,
        items: [{ sku, title: customiseItem.productTitle, isCustom: !!isCustom, isMto: !isCustom, isCustomSize: !!isCustom }]
      });
      setCustDeliveryResults(res.data);
    } catch (e) {
      setCustDeliveryResults({ success: false, results: [{ deliverable: false, error: 'Failed to check delivery' }] });
    }
    setCustDeliveryLoading(false);
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
          <div style={{ fontSize: '28px', marginBottom: '8px', animation: 'spin 1s linear infinite' }}>&#9696;</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Loading inventory...
        </div>
      )}

      {!loading && inventoryData && (() => {
        // For stores: count continueSelling items with qty<=0 as OOS, not inStock
        const mtoCount = inventoryData.items.filter(i => i.continueSelling && i.quantity <= 0).length;
        const adjustedInStock = isWarehouse ? inventoryData.summary.inStock : inventoryData.summary.inStock - mtoCount;
        const adjustedOutOfStock = isWarehouse ? inventoryData.summary.outOfStock : inventoryData.summary.outOfStock + mtoCount;

        return (
        <div>
          <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <SummaryCard label="Total Variants" value={inventoryData.summary.totalVariants} />
            <SummaryCard label="In Stock" value={adjustedInStock} color="#27ae60" />
            {isWarehouse && <SummaryCard label="MTO (Made to Order)" value={mtoCount} color="#7c3aed" />}
            <SummaryCard label="Out of Stock" value={adjustedOutOfStock} color="#c0392b" />
            <SummaryCard label="Low Stock (≤5)" value={inventoryData.summary.lowStock} color="#e67e22" />
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by product, variant, or SKU..."
              value={inventorySearch}
              onChange={e => setInventorySearch(e.target.value)}
              style={{
                flex: 1, minWidth: '200px', padding: '10px 14px', border: '2px solid rgba(200, 131, 70, 0.12)',
                borderRadius: '10px', fontSize: '14px', outline: 'none'
              }}
              onFocus={e => e.target.style.borderColor = '#c88346'}
              onBlur={e => e.target.style.borderColor = 'rgba(200, 131, 70, 0.12)'}
            />
            {isWarehouse && (
              <button
                onClick={() => setInventoryFilter(inventoryFilter === 'mto' ? 'all' : 'mto')}
                style={{
                  padding: '8px 16px', border: inventoryFilter === 'mto' ? '2px solid #7c3aed' : '1px solid rgba(124, 58, 237, 0.2)',
                  borderRadius: '10px', fontSize: '13px', fontWeight: '700',
                  color: inventoryFilter === 'mto' ? '#fff' : '#7c3aed',
                  background: inventoryFilter === 'mto' ? '#7c3aed' : '#f3f0ff',
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                📦 MTO
              </button>
            )}
            <div style={{ display: 'flex', gap: '6px' }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'out', label: 'Out of Stock' },
                { key: 'low', label: 'Low Stock' },
                { key: 'in', label: 'In Stock' }
              ].map(f => (
                <button key={f.key} onClick={() => setInventoryFilter(f.key)}
                  style={{
                    padding: '8px 14px', border: inventoryFilter === f.key ? '2px solid #c88346' : '1px solid rgba(200, 131, 70, 0.12)',
                    borderRadius: '8px', fontSize: '12px', fontWeight: inventoryFilter === f.key ? '700' : '500',
                    color: inventoryFilter === f.key ? '#fff' : '#5a4a3a',
                    background: inventoryFilter === f.key ? '#c88346' : '#ffffff',
                    cursor: 'pointer'
                  }}>
                  {f.label}
                </button>
              ))}
            </div>
            {!isWarehouse && (
              <button
                onClick={() => {
                  const rows = filteredInventory.map(item => ({
                    'Product': item.productTitle,
                    'Variant': item.variantTitle || '-',
                    'SKU': item.sku || '-',
                    'Price': item.price,
                    'Quantity': item.quantity,
                    'Status': item.quantity <= 0 ? 'Out of Stock' : item.quantity <= 5 ? 'Low Stock' : 'In Stock'
                  }));
                  if (rows.length === 0) { alert('No data to export'); return; }
                  const headers = Object.keys(rows[0]);
                  const csvContent = [headers.join(','), ...rows.map(r => headers.map(h => `"${String(r[h] || '').replace(/"/g, '""')}"`).join(','))].join('\n');
                  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `inventory_${new Date().toISOString().slice(0,10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                style={{
                  padding: '8px 16px', border: '1px solid rgba(39, 174, 96, 0.3)',
                  borderRadius: '10px', fontSize: '12px', fontWeight: '600',
                  color: '#27ae60', background: '#f0fdf4',
                  cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                📥 Download Excel
              </button>
            )}
          </div>

          {/* Collection tabs */}
          <div style={{
            display: 'flex', gap: '6px', marginBottom: '16px', overflowX: 'auto',
            paddingBottom: '4px', scrollbarWidth: 'none', msOverflowStyle: 'none'
          }}>
            {COLLECTION_TABS.map(tab => (
              <button
                key={tab.handle}
                onClick={() => { setInventoryCollection(tab.handle); setInventorySearch(''); setMtoMap({}); }}
                style={{
                  padding: '6px 14px',
                  border: inventoryCollection === tab.handle ? '2px solid #c88346' : '1px solid rgba(200, 131, 70, 0.12)',
                  borderRadius: '14px', fontSize: '12px',
                  fontWeight: inventoryCollection === tab.handle ? '700' : '500',
                  color: inventoryCollection === tab.handle ? '#fff' : '#5a4a3a',
                  background: inventoryCollection === tab.handle ? '#c88346' : '#f8f4ef',
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span>{filteredInventory.length} item{filteredInventory.length !== 1 ? 's' : ''}</span>
            {easyEcom.loading ? (
              <span style={{ color: '#0e7490' }}>⟳ Loading EasyEcom inventory...</span>
            ) : easyEcom.mapped ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#0e7490' }}>
                  EasyEcom: {easyEcom.locationName}
                  {easyEcom.fetchedAt && ` (as of ${new Date(easyEcom.fetchedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })})`}
                  {easyEcom.refreshing && ' — refreshing…'}
                </span>
                <button
                  onClick={() => fetchEasyEcom(true)}
                  disabled={easyEcom.refreshing}
                  style={{
                    padding: '2px 10px', fontSize: '11px', fontWeight: '600', border: '1px solid rgba(14, 116, 144, 0.3)',
                    borderRadius: '8px', background: '#ecfeff', color: '#0e7490',
                    cursor: easyEcom.refreshing ? 'not-allowed' : 'pointer', opacity: easyEcom.refreshing ? 0.6 : 1
                  }}
                >
                  ⟳ Refresh
                </button>
              </span>
            ) : easyEcom.error ? (
              <span style={{ color: '#c0392b' }}>{easyEcom.error}</span>
            ) : (
              <span style={{ color: '#b5a99a' }}>This location is not mapped to EasyEcom</span>
            )}
          </div>

          {/* Inventory table */}
          <div style={{ background: '#faf7f3',  borderRadius: '16px', border: '1px solid #ece0d1', maxHeight: '600px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(200, 131, 70, 0.12)', position: 'sticky', top: 0, background: '#ffffff',  zIndex: 1 }}>
                  <th style={{ ...thStyle, width: '50px' }}></th>
                  <th style={thStyle}>Product</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>MRP / Price</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Discount</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Stock</th>
                  {easyEcom.mapped && <th style={{ ...thStyle, textAlign: 'center' }}>EasyEcom</th>}
                  <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.map((item, i) => {
                  const isOOS = item.quantity <= 0 && (!item.continueSelling || !isWarehouse);
                  const isLow = item.quantity > 0 && item.quantity <= 5;
                  const isMtoAtZeroInventory = isWarehouse && item.quantity <= 0 && item.continueSelling && !!mtoMap[String(item.variantId)];
                  const mrp = item.compareAtPrice ? parseFloat(item.compareAtPrice) : null;
                  const price = parseFloat(item.price);
                  const discountPct = mrp && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #fdf8f3', opacity: isOOS ? 0.6 : 1 }}>
                      <td style={{ ...tdStyle, padding: '8px' }}>
                        {item.image ? (
                          <img src={item.image} alt="" style={{ width: '40px', height: '40px', borderRadius: '10px', objectFit: 'cover' }} loading="lazy" />
                        ) : (
                          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f5f0eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#d4c8ba' }}>&#128247;</div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '260px' }}>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#3d2b1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.productTitle}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '2px', alignItems: 'center' }}>
                          {item.variantTitle !== 'Default Title' && (
                            <span style={{ fontSize: '11px', color: '#8b7d6e' }}>{item.variantTitle}</span>
                          )}
                          {item.sku && (
                            <span style={{ fontSize: '10px', color: '#b5a99a', fontFamily: 'monospace', background: '#f5f0eb', padding: '1px 5px', borderRadius: '3px' }}>
                              {item.sku}
                            </span>
                          )}
                          {isMtoAtZeroInventory && (
                            <span style={{
                              fontSize: '9px', fontWeight: '700', color: '#fff',
                              background: '#7c3aed', padding: '1px 6px', borderRadius: '4px',
                              letterSpacing: '0.5px'
                            }}>
                              MTO
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {mrp && mrp > price ? (
                          <div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', textDecoration: 'line-through' }}>
                              {formatCurrency(mrp)}
                            </div>
                            <div style={{ fontWeight: '700', fontSize: '13px', color: '#3d2b1f' }}>
                              {formatCurrency(price)}
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontWeight: '700', fontSize: '13px', color: '#3d2b1f' }}>
                            {formatCurrency(price)}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {discountPct > 0 ? (
                          <span style={{
                            padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700',
                            background: '#eaf7ee', color: '#27ae60'
                          }}>
                            {discountPct}% off
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#d4c8ba' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600',
                          background: isOOS ? '#fdeaea' : isLow ? '#fef3e2' : '#eaf7ee',
                          color: isOOS ? '#c0392b' : isLow ? '#e67e22' : '#27ae60'
                        }}>
                          {isOOS ? 'Out' : isMtoAtZeroInventory ? 'MTO' : (isWarehouse && item.continueSelling && item.quantity <= 0) ? 'Available' : item.quantity}
                        </span>
                      </td>
                      {easyEcom.mapped && (() => {
                        const ee = item.sku ? easyEcom.quantities[item.sku] : null;
                        const eeQty = ee ? ee.available : null;
                        const differs = ee && Number(eeQty) !== Number(item.quantity);
                        return (
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {ee ? (
                              <span style={{
                                padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600',
                                background: differs ? '#fef3e2' : '#ecfeff',
                                color: differs ? '#e67e22' : '#0e7490'
                              }} title={`${differs ? 'Differs from Shopify stock' : 'Matches Shopify stock'}${ee.live ? ' (fetched live — not in EasyEcom catalog feed)' : ''}`}>
                                {eeQty}
                              </span>
                            ) : (
                              <span style={{ fontSize: '11px', color: '#d4c8ba' }} title="SKU not found in EasyEcom at this location">—</span>
                            )}
                          </td>
                        );
                      })()}
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#8b7d6e', fontSize: '12px' }}>
                        {item.quantity > 0 ? formatCurrency(item.quantity * price) : '-'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          {(() => {
                            const t = (item.productTitle || '').toLowerCase();
                            const customizableNames = ['original mattress','hybrid latex mattress','latex ortho mattress','founders mattress','founder\'s mattress','ultima memory foam mattress','ultima natural latex','baby mattress','memory foam mattress topper','2 inch memory foam mattress topper','aeroflow mattress topper','natural latex mattress topper','cloud mattress'];
                            const excludeNames = ['tri fold mattress','tri-fold mattress'];
                            const canCustomize = locationType !== 'store' && customizableNames.some(name => t.includes(name)) && !excludeNames.some(name => t.includes(name));
                            return canCustomize ? (
                              <button
                                onClick={() => handleOpenCustomise(item)}
                                style={{
                                  padding: '5px 10px', fontSize: '11px', fontWeight: '600',
                                  border: '1px solid #c88346', borderRadius: '10px',
                                  background: '#f8f4ef', color: '#c88346', cursor: 'pointer',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Customise
                              </button>
                            ) : null;
                          })()}
                          <button
                            onClick={() => handleOpenDelivery(item)}
                            disabled={!item.sku}
                            style={{
                              padding: '5px 10px', fontSize: '11px', fontWeight: '600',
                              border: '1px solid #6366f1', borderRadius: '10px',
                              background: '#eef2ff', color: '#6366f1',
                              cursor: item.sku ? 'pointer' : 'not-allowed',
                              opacity: item.sku ? 1 : 0.5,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Delivery
                          </button>
                          {easyEcom.mapped && (
                            <button
                              onClick={() => handleEasyEcomSync(item)}
                              disabled={!item.sku || !easyEcom.quantities[item.sku] || !!syncingSkus[item.sku]}
                              title="Push EasyEcom quantity to Shopify for this location"
                              style={{
                                padding: '5px 10px', fontSize: '11px', fontWeight: '600',
                                border: '1px solid #0e7490', borderRadius: '10px',
                                background: '#ecfeff', color: '#0e7490',
                                cursor: (item.sku && easyEcom.quantities[item.sku] && !syncingSkus[item.sku]) ? 'pointer' : 'not-allowed',
                                opacity: (item.sku && easyEcom.quantities[item.sku]) ? 1 : 0.5,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {syncingSkus[item.sku] ? 'Syncing...' : '⟳ Sync'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {/* ===== CUSTOMISE MODAL ===== */}
      {showCustomiseModal && customiseItem && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 15, 30, 0.55)',  display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setShowCustomiseModal(false)}
        >
          <div
            style={{
              background: '#ffffff',   borderRadius: '14px', padding: '28px',
              width: '460px', maxWidth: '90vw', boxShadow: '0 24px 64px rgba(26, 26, 46, 0.2), 0 8px 24px rgba(26, 26, 46, 0.1)',
              maxHeight: '90vh', overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#3d2b1f', marginBottom: '4px' }}>
              Customize {(customiseItem.productTitle || '').toLowerCase().includes('topper') ? 'Topper' : 'Mattress'} Size
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
              {customiseItem.productTitle}
              {customiseItem.variantTitle !== 'Default Title' ? ` — ${customiseItem.variantTitle}` : ''}
            </p>

            {standardDims ? (
              <>
                <div style={{
                  background: '#f5f0eb', borderRadius: '10px', padding: '12px 16px',
                  marginBottom: '20px', fontSize: '13px', color: '#5a4a3a'
                }}>
                  Selected size: <strong>{standardDims.length} x {standardDims.width}{standardDims.height ? ` x ${standardDims.height}` : ''}</strong>
                  <span style={{ color: '#9ca3af', marginLeft: '8px' }}>{standardDims.height ? '(L x W x H)' : '(L x W)'}</span>
                </div>

                {(() => {
                  const pTitle = (customiseItem.productTitle || '').toLowerCase();
                  const isFounders = pTitle.includes("founder's mattress");
                  const isTopper = pTitle.includes('mattress topper');
                  const isAeroflowOrLatexTopper = pTitle.includes('aeroflow') || pTitle.includes('natural latex mattress topper');
                  const minL = 60, maxL = isTopper ? 78 : 84, minW = 24;
                  const maxW = (isFounders || isAeroflowOrLatexTopper) ? 72 : 78;
                  const l = parseInt(customLength), w = parseInt(customWidth);
                  const lValid = !isNaN(l) && l >= minL && l <= maxL;
                  const wValid = !isNaN(w) && w >= minW && w <= maxW;
                  const lHasValue = customLength !== '' && customLength !== undefined;
                  const wHasValue = customWidth !== '' && customWidth !== undefined;
                  return (
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                      Length <span style={{ color: '#9ca3af', fontWeight: '400' }}>({minL}–{maxL})</span>
                    </label>
                    <input
                      type="number" min={minL} max={maxL} step={1} value={customLength}
                      onChange={e => {
                        setCustomLength(e.target.value);
                        handleDimensionChange(e.target.value, customWidth, customiseItem.variants || []);
                      }}
                      style={{
                        width: '100%', padding: '10px 14px',
                        border: `2px solid ${lHasValue ? (lValid ? '#22c55e' : '#ef4444') : 'rgba(200, 131, 70, 0.12)'}`,
                        borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                        outline: 'none', boxSizing: 'border-box',
                        color: lHasValue ? (lValid ? '#166534' : '#dc2626') : '#3d2b1f'
                      }}
                    />
                    {lHasValue && !lValid && (
                      <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', fontWeight: '600' }}>
                        Must be {minL}–{maxL} inches
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                      Width <span style={{ color: '#9ca3af', fontWeight: '400' }}>({minW}–{maxW})</span>
                    </label>
                    <input
                      type="number" min={minW} max={maxW} step={1} value={customWidth}
                      onChange={e => {
                        setCustomWidth(e.target.value);
                        handleDimensionChange(customLength, e.target.value, customiseItem.variants || []);
                      }}
                      style={{
                        width: '100%', padding: '10px 14px',
                        border: `2px solid ${wHasValue ? (wValid ? '#22c55e' : '#ef4444') : 'rgba(200, 131, 70, 0.12)'}`,
                        borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                        outline: 'none', boxSizing: 'border-box',
                        color: wHasValue ? (wValid ? '#166534' : '#dc2626') : '#3d2b1f'
                      }}
                    />
                    {wHasValue && !wValid && (
                      <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', fontWeight: '600' }}>
                        Must be {minW}–{maxW} inches
                      </div>
                    )}
                  </div>
                  <div style={{ width: '80px', display: standardDims.height ? 'block' : 'none' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                      Height
                    </label>
                    <input
                      type="number" value={standardDims.height || ''} disabled
                      style={{
                        width: '100%', padding: '10px 14px', border: '2px solid rgba(200, 131, 70, 0.12)',
                        borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                        outline: 'none', boxSizing: 'border-box',
                        background: '#f3f4f6', color: '#9ca3af'
                      }}
                    />
                  </div>
                </div>
                  );
                })()}

                {matchedVariant && matchedDims ? (
                  <div style={{
                    background: '#eaf7ee', borderRadius: '10px', padding: '12px 16px',
                    marginBottom: '20px', fontSize: '13px', color: '#166534'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      ✓ Matched variant: {matchedVariant.title}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Size: {matchedDims.length} x {matchedDims.width}{matchedDims.height ? ` x ${matchedDims.height}` : ''}</span>
                      <div style={{ textAlign: 'right' }}>
                        {matchedVariant.compareAtPrice && parseFloat(matchedVariant.compareAtPrice) > parseFloat(matchedVariant.price) && (
                          <div style={{ fontSize: '11px', color: '#6b7280', textDecoration: 'line-through' }}>
                            MRP: {formatCurrency(matchedVariant.compareAtPrice)}
                          </div>
                        )}
                        <span style={{ fontWeight: '700' }}>Price: {formatCurrency(matchedVariant.price)}</span>
                      </div>
                    </div>
                  </div>
                ) : (customLength && customWidth) ? (
                  <div style={{
                    background: '#fee2e2', borderRadius: '10px', padding: '12px 16px',
                    marginBottom: '20px', fontSize: '13px', color: '#dc2626'
                  }}>
                    No matching variant found for the entered dimensions. Try smaller values.
                  </div>
                ) : null}
              </>
            ) : (
              <div style={{
                background: '#f5f0eb', borderRadius: '10px', padding: '16px',
                marginBottom: '20px', fontSize: '13px', color: '#5a4a3a', textAlign: 'center'
              }}>
                <div style={{ marginBottom: '8px', fontSize: '20px' }}>📐</div>
                This product does not have customizable dimensions.<br />
                Only mattress products with L x W x H variants can be customized.
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowCustomiseModal(false)}
                style={{
                  flex: 1, padding: '12px 20px', background: '#f5f0eb', color: '#3d2b1f',
                  border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>

            {/* Delivery check inside Customise modal */}
            <div style={{ borderTop: '1px solid rgba(200, 131, 70, 0.12)', marginTop: '20px', paddingTop: '20px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#3d2b1f', marginBottom: '10px' }}>Check Delivery Timeline</h4>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <input
                  type="text"
                  maxLength={6}
                  value={custDeliveryPincode}
                  onChange={e => {
                    setCustDeliveryPincode(e.target.value.replace(/\D/g, ''));
                    setCustDeliveryResults(null);
                  }}
                  placeholder="Enter 6-digit pincode"
                  style={{
                    flex: 1, padding: '10px 14px', border: '2px solid rgba(200, 131, 70, 0.12)',
                    borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                    outline: 'none', boxSizing: 'border-box', letterSpacing: '2px'
                  }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => e.target.style.borderColor = 'rgba(200, 131, 70, 0.12)'}
                  onKeyDown={e => { if (e.key === 'Enter') handleCustCheckDelivery(); }}
                />
                <button
                  onClick={handleCustCheckDelivery}
                  disabled={custDeliveryLoading || custDeliveryPincode.length !== 6}
                  style={{
                    padding: '10px 20px', background: custDeliveryPincode.length === 6 ? '#6366f1' : '#d1d5db',
                    color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px',
                    fontWeight: '700', cursor: custDeliveryPincode.length === 6 ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {custDeliveryLoading ? 'Checking...' : 'Check'}
                </button>
              </div>

              {custDeliveryResults && custDeliveryResults.results && custDeliveryResults.results.map((r, idx) => (
                <div key={idx} style={{
                  background: r.deliverable ? '#eaf7ee' : '#fdeaea',
                  borderRadius: '10px', padding: '14px 16px', fontSize: '13px',
                  color: r.deliverable ? '#166534' : '#991b1b'
                }}>
                  {r.deliverable ? (
                    <div>
                      <div style={{ fontWeight: '700', marginBottom: '6px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        ✓ Deliverable to {custDeliveryPincode}
                        {r.isMto ? <span style={{ color: '#fff', background: '#7c3aed', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px' }}>MTO</span> : r.isCustomSize ? <span style={{ color: '#fff', background: '#c88346', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px' }}>Custom</span> : null}
                      </div>
                      {r.deliveryDate && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '18px' }}>📦</span>
                          <div>
                            <div style={{ fontWeight: '600' }}>Expected Delivery</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#15803d' }}>
                              {new Date(r.deliveryDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                          </div>
                        </div>
                      )}
                      {r.isMto && r.isMtoMsg && <div style={{ marginTop: '6px', fontSize: '11px', color: '#7c3aed', fontStyle: 'italic' }}>{r.isMtoMsg}</div>}
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontWeight: '700', marginBottom: '4px' }}>
                        ✗ Not deliverable to {custDeliveryPincode}
                      </div>
                      <div style={{ fontSize: '12px' }}>{r.error || 'This product cannot be delivered to the entered pincode.'}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== DELIVERY TIMELINE MODAL ===== */}
      {showDeliveryModal && deliveryItem && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 15, 30, 0.55)',  display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setShowDeliveryModal(false)}
        >
          <div
            style={{
              background: '#ffffff',   borderRadius: '14px', padding: '28px',
              width: '440px', maxWidth: '90vw', boxShadow: '0 24px 64px rgba(26, 26, 46, 0.2), 0 8px 24px rgba(26, 26, 46, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#3d2b1f', marginBottom: '4px' }}>
              Check Delivery Timeline
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
              {deliveryItem.productTitle}
              {deliveryItem.variantTitle !== 'Default Title' ? ` — ${deliveryItem.variantTitle}` : ''}
              {deliveryItem.sku ? ` (${deliveryItem.sku})` : ''}
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                Customer Pincode
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  maxLength={6}
                  value={deliveryPincode}
                  onChange={e => {
                    setDeliveryPincode(e.target.value.replace(/\D/g, ''));
                    setDeliveryResults(null);
                  }}
                  placeholder="Enter 6-digit pincode"
                  style={{
                    flex: 1, padding: '10px 14px', border: '2px solid rgba(200, 131, 70, 0.12)',
                    borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                    outline: 'none', boxSizing: 'border-box', letterSpacing: '2px'
                  }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => e.target.style.borderColor = 'rgba(200, 131, 70, 0.12)'}
                  onKeyDown={e => { if (e.key === 'Enter') handleCheckDelivery(); }}
                  autoFocus
                />
                <button
                  onClick={handleCheckDelivery}
                  disabled={deliveryLoading || deliveryPincode.length !== 6}
                  style={{
                    padding: '10px 20px', background: deliveryPincode.length === 6 ? '#6366f1' : '#d1d5db',
                    color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px',
                    fontWeight: '700', cursor: deliveryPincode.length === 6 ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {deliveryLoading ? 'Checking...' : 'Check'}
                </button>
              </div>
            </div>

            {deliveryResults && (
              <div style={{ marginBottom: '20px' }}>
                {deliveryResults.results && deliveryResults.results.map((r, idx) => (
                  <div key={idx} style={{
                    background: r.deliverable ? '#eaf7ee' : '#fdeaea',
                    borderRadius: '10px', padding: '14px 16px', fontSize: '13px',
                    color: r.deliverable ? '#166534' : '#991b1b'
                  }}>
                    {r.deliverable ? (
                      <div>
                        <div style={{ fontWeight: '700', marginBottom: '6px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          ✓ Deliverable to {deliveryPincode}
                          {r.isMto ? <span style={{ color: '#fff', background: '#7c3aed', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px' }}>MTO</span> : r.isCustomSize ? <span style={{ color: '#fff', background: '#c88346', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px' }}>Custom</span> : null}
                        </div>
                        {r.deliveryDate && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '18px' }}>📦</span>
                            <div>
                              <div style={{ fontWeight: '600' }}>Expected Delivery</div>
                              <div style={{ fontSize: '16px', fontWeight: '700', color: '#15803d' }}>
                                {new Date(r.deliveryDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                              </div>
                            </div>
                          </div>
                        )}
                        {r.isMto && r.isMtoMsg && <div style={{ marginTop: '6px', fontSize: '11px', color: '#7c3aed', fontStyle: 'italic' }}>{r.isMtoMsg}</div>}
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontWeight: '700', marginBottom: '4px' }}>
                          ✗ Not deliverable to {deliveryPincode}
                        </div>
                        <div style={{ fontSize: '12px' }}>{r.error || 'This product cannot be delivered to the entered pincode.'}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowDeliveryModal(false)}
              style={{
                width: '100%', padding: '12px', background: '#f5f0eb', color: '#3d2b1f',
                border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px', fontSize: '14px',
                fontWeight: '600', cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#8b7d6e', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '10px 12px', fontSize: '13px', color: '#3d2b1f' };

export default Inventory;
